import { AlignHorizontalLeft, AlignVerticalTop } from "@mui/icons-material";
import {
  Box,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  useColorScheme,
  useTheme,
} from "@mui/material";
import {
  Background,
  BackgroundVariant,
  Controls,
  Edge,
  MiniMap,
  Node,
  Panel,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesInitialized,
  useNodesState,
  useOnSelectionChange,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import React, { useCallback, useEffect, useState } from "react";
import { DetailedCityEscape } from "../../directus/collections/cityEscape.type";
import { MatchFilter } from "../../libs/statsFilters";
import { LangSwitchOption } from "../../types/misc";
import { useCityEscapeFlow } from "./convertCityEscapeToFlowData";
import { useLayout } from "./layout";
import {
  EpilogueNode,
  ExpandableSceneNode,
  PrologueNode,
  SceneOutputExitNode,
} from "./nodes";
import { CityEscapeElementNodeData } from "./types";

interface CityEscapeFlowChartProps {
  cityEscape: DetailedCityEscape;
  filters?: MatchFilter;
  lang: LangSwitchOption;
  width?: string | number;
  height?: string | number;
  direction?: "DOWN" | "UP" | "RIGHT" | "LEFT";
  className?: string;
  minimap?: boolean;
  controls?: boolean;
  selectedSceneId?: string;
  defaultZoom?: number;
  onSelection?: (node: Node) => void;
}

// Node types
const nodeTypes = {
  scene: ExpandableSceneNode,
  prologue: PrologueNode,
  epilogue: EpilogueNode,
  exit: SceneOutputExitNode,
};

// Internal component that uses ReactFlow hooks
const CityEscapeFlowChartInternal: React.FC<CityEscapeFlowChartProps> = ({
  cityEscape,
  filters,
  lang,
  width = "100%",
  height = 400,
  direction = "DOWN",
  className,
  minimap = false,
  controls: _controls = true, // Commented out for now as not used
  selectedSceneId,
  defaultZoom = 1,
  onSelection,
}) => {
  const theme = useTheme();
  const colorScheme = useColorScheme();
  const [flowDirection, setFlowDirection] = useState<
    "DOWN" | "UP" | "RIGHT" | "LEFT"
  >(direction);

  // State for expanded scenes
  const [expandedScenes, setExpandedScenes] = useState<Set<string>>(new Set());
  const [nodes, setNodes, onNodesChange] = useNodesState<
    Node<CityEscapeElementNodeData>
  >([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const nodesInitialized = useNodesInitialized({ includeHiddenNodes: true });
  const layoutedNodes = useLayout(nodes, edges, {
    direction: flowDirection,
    nodesInitialized,
  });
  const { getNodes, updateNodeData } = useReactFlow();

  // Convert city escape data to flow chart format with proper sub-flows
  const cityEscapeFlow = useCityEscapeFlow(cityEscape, {
    theme,
    direction: flowDirection,
  });

  useEffect(() => {
    setNodes(cityEscapeFlow.data?.nodes || []);
    setEdges(cityEscapeFlow.data?.edges || []);
    console.log("Initial nodes and edges set");
  }, [cityEscapeFlow.data, setNodes, setEdges]);

  useEffect(() => {
    if (layoutedNodes.data) {
      setNodes(
        layoutedNodes.data.map((node) => ({
          ...node,
          data: {
            ...node.data,
            filters: filters,
            activeLang: lang,
            expandCard: () => {
              setExpandedScenes((prev) => new Set(prev).add(node.id));
            },
            collapseCard: () => {
              setExpandedScenes((prev) => {
                const newSet = new Set(prev);
                newSet.delete(node.id);
                return newSet;
              });
            },
          },
        }))
      );
    }
  }, [layoutedNodes.data, getNodes, setNodes]);

  useEffect(() => {
    for (const node of getNodes()) {
      if (["expanded", "scene"].includes(node.type || "")) {
        const isExpanded = expandedScenes.has(node.id);
        updateNodeData(node.id, {
          isExpanded: isExpanded,
          isExpandable: !isExpanded,
        });
      }
    }
  }, [expandedScenes, setNodes]);

  useEffect(() => {
    for (const node of getNodes()) {
      if (node.data.activeLang !== lang) {
        updateNodeData(node.id, { activeLang: lang });
      }
    }
  }, [lang]);

  useEffect(() => {
    for (const node of getNodes()) {
      updateNodeData(node.id, { filters: filters });
    }
  }, [filters]);

  const onNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      // Only handle scene nodes to expand/collapse their steps
      if (node.data.nodeType === "scene" && node.data.scene) {
        const sceneId = node.id;
        setExpandedScenes((prev) => {
          const newSet = new Set(prev);

          if (prev.has(sceneId)) {
            newSet.delete(sceneId); // Collapse
          } else {
            newSet.add(sceneId); // Expand
          }
          return newSet;
        });
      }
    },
    [setExpandedScenes]
  );

  const onSelectionChange = useCallback(
    ({ nodes }: { nodes: Node[]; edges: Edge[] }) => {
      onSelection?.(nodes[0]);
    },
    [onSelection]
  );

  useOnSelectionChange({
    onChange: onSelectionChange,
  });

  return (
    <Box
      className={className}
      sx={{
        width,
        height,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        // backgroundColor: theme.palette.background.default,
      }}
    >
      <ReactFlow
        defaultNodes={nodes}
        defaultEdges={edges}
        nodes={nodes}
        edges={edges}
        // onNodeClick={onNodeClick}
        nodeTypes={nodeTypes}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={true}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        defaultViewport={{ zoom: defaultZoom, x: 0, y: 0 }}
        fitViewOptions={{
          maxZoom: defaultZoom,
        }}  
        colorMode={colorScheme.mode}
      >
        <Background id="ce-flow-bg" variant={BackgroundVariant.Dots}  />
        <Controls position="top-left" />

        {/* Instructions panel
        <Panel position="bottom-left">
          <Box
            sx={{
              backgroundColor: theme.palette.background.paper,
              padding: 1,
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: theme.shadows[1],
              maxWidth: 250,
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.7rem" }}
            >
              💡 Clicca su una scena per espandere/comprimere i suoi step
            </Typography>
          </Box>
        </Panel> */}

        {minimap && (
          <>
            <MiniMap
              position="bottom-right"
              style={{
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
              }}
              nodeColor={theme.palette.primary.main}
              zoomable={true}
              pannable={true}
            />
            <Panel position="top-right">
              <Stack
                direction="row"
                spacing={1}
                alignItems="flex-end"
                justifyContent="flex-end"
              >
                <ToggleButtonGroup
                  value={flowDirection}
                  exclusive
                  onChange={(e, newAlignment) => setFlowDirection(newAlignment)}
                  aria-label="text alignment"
                >
                  <ToggleButton value="RIGHT" aria-label="left aligned">
                    <AlignHorizontalLeft />
                  </ToggleButton>
                  <ToggleButton value="DOWN" aria-label="centered">
                    <AlignVerticalTop />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
              <Box
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  padding: 1,
                  borderRadius: 1,
                  fontSize: "12px",
                  color: theme.palette.text.secondary,
                }}
              >
                {cityEscapeFlow.data?.nodes.length} scene,
                {cityEscapeFlow.data?.edges.length} connessioni
              </Box>
            </Panel>
          </>
        )}
        

      </ReactFlow>
    </Box>
  );
};

// Main component wrapped with ReactFlowProvider
export const CityEscapeFlowChart: React.FC<CityEscapeFlowChartProps> = (
  props
) => {
  return (
    <ReactFlowProvider>
      <CityEscapeFlowChartInternal {...props} />
    </ReactFlowProvider>
  );
};

export default CityEscapeFlowChart;
